<template>
  <ModuleInfoModal
      :is-open="isModuleInfoModalOpen"
      :module="selectedModule"
      :pinableSems=null
      :currentSemester=null
      :search=true
      :modal-name="modalName"
      :is-passed="modalName === 'PassedModulesModal' || modalName === 'SaveSemesterChangedModal'"
      :baseModuleModal = "openedInBaseModuleModal"
      :unavailableModules="unavailableModules"
      :unavailableReason="unavailableReason"
      @close="closeModuleInfoModal"
      @select-module="selectModule"
      @specify-module="handleSpecifyModule"
    />
  <base-modal
      :icon-modal="iconModal"
      :modal-subtitle="modalSubtitle"
      :modal-title="modalTitle"
      :text-confirm="baseTextConfirm"
      :text-discard="textDiscard"
      :closing-offered="closingOffered"
      :confirmation-disabled="confirmationDisabled"
      :discarding-disabled="!changeDetected"
      :confirmation-required="true"
      :modal-name="modalName"
      :close-on-confirm="closeOnConfirm"
      :discarding-offered="discardingOffered"
      :length="650"
      :class="modalClass"
      @confirm="confirm"
  >
    <template #body>
      <div v-if="this.$props.showInput" class="modal-entry">
        <ModuleSearchDropdown 
          :groupedModules="groupedModules"
          :selectableModules="selectableModules"
          :hasStructureGroups="hasStructureGroups"
          :unavailableReason="unavailableReason"
          :moduleInput="moduleInput"
          :isInfoModalOpen="isModuleInfoModalOpen"
          :forceCloseDropdown="forceCloseDropdown"
          :currentSemester="currentSemester"
          @update:moduleInput="moduleInput = $event"
          @info-click="module => openModuleInfoModal(module, false)"
          @select-module="clickMarkModule"
        />
      </div>
      <div class="message-wrapper">
        <transition name="fade">
          <div v-if="message" class="message">
            <span class="check-icon"></span> {{ message }}
          </div>
        </transition>
      </div>
      <div class="modal-result">
        <transition-group class="module-grid" name="fade" tag="div">
          <div v-for="module in displayedMarkedModules" :key="module.id" :class="['module', highlightClass(module.id)]">
            <div class="module-content">
              <div class="left-content">
                <div class="module-detail">{{ module.mock ? '' : module.number }}</div>
                <div class="module-name">{{ module.name }}</div>
                <div class="module-detail">{{
                    module.mock && this.oneModulePerMockName ? '' : `${module.ects} ECTS`
                  }}
                </div>
              </div>
              <div class="right-content">
                <div class="right-control">
                  <div class="icon-container tooltip-container">
                    <span class="material-symbols-outlined" @click="unmarkModule(module)">{{ iconRemove }}</span>
                    <div class="tooltip-text bottom">{{ textRemove }}</div>
                  </div>
                </div>
                <div class="material-symbols-outlined icon-container" @click="() => openModuleInfoModal(module, true)">info</div>
              </div>
            </div>
          </div>
        </transition-group>
      </div>
      <div v-if="textHint.length !== 0" class="text-hint">
        <strong>Note:&ensp;</strong>{{ textHint }}
      </div>
      
    </template>
  </base-modal>
  
</template>


<script>
import ModuleInfoModal from '@/components/modals/ModuleInfoModal.vue';
import ModuleSearchDropdown from '@/components/modals/ModuleSearchDropdown.vue';
import BaseModal from "@/components/modals/BaseModal.vue";
import {mapState} from "vuex";
import { reactive } from 'vue';
import {
  prioritiesOfStructureElements,
  modulesWithStructures,
  selectedStructures,
  getSelectableModules,
  getGroupedModules,
  getStructureColorClass,
  getDependentModules
} from '@/utils/moduleGrouping';

export default {
  name: "BaseModuleModal",
  components: {BaseModal,ModuleInfoModal,ModuleSearchDropdown},
  emits: ['confirmOperation'],
  props: {
    modalName: String,
    moduleType: {
      type: String,
      default: ""
    },
    iconModal: {
      type: String,
    },
    modalTitle: {
      type: String,
    },
    modalSubtitle: {
      type: String,
      default: ""
    },
    textDiscard: {
      type: String,
      default: "Discard changes"
    },
    textConfirm: {
      type: String,
      default: ""
    },
    unavailableReason: {
      type: String,
      default: "inactive"
    },
    iconRemove: {
      type: String,
      default: "delete_outline"
    },
    textRemove: {
      type: String,
      default: "Remove"
    },
    textHint: {
      type: String,
      default: ""
    },
    unavailableModules: {
      type: Array,
      default: () => []
    },
    hiddenModules: {
      type: Array,
      default: () => []
    },
    addedModules: {
      type: Array,
      default: () => []
    },
    showInput: {
      type: Boolean,
      default: true
    },
    closingOffered: {
      type: Boolean,
      default: true
    },
    confirmationAlwaysEnabled: {
      type: Boolean,
      default: false
    },
    closeOnConfirm: {
      type: Boolean,
      default: true
    },
    discardingOffered: {
      type: Boolean,
      default: true
    },
    oneModulePerMockName: {
      type: Boolean,
      default: false
    },
    extraGroup: {
      type: Object,
      default: null
    },
    markExtraGroup: {
      type: Boolean,
      default: false
    },
    showNotOfferedOption: {
      type: Boolean,
      default: false
    },
    highlightDarkBlue: {
      type: Array,
      default: () => []
    },
    highlightBlue: {
      type: Array,
      default: () => []
    },
    modalClass: {
      type: String,
      default: "base-module-modal"
    },
  },
  data() {
   return {
    ...this.initialData(),
    collapsedGroups: reactive({}),
    isModuleInfoModalOpen: false,
    openedInBaseModuleModal: false,
    selectedModule: null, 
  };
  },
  computed: {
    ...mapState(["program", "preferences", "currentModal"]),
    baseTextConfirm() {
      if (this.$props.textConfirm.length !== 0) {
        return this.$props.textConfirm;
      }
      if (this.$props.confirmationAlwaysEnabled && this.markedModules.length === 0) {
        return `Continue without ${this.$props.moduleType ? this.$props.moduleType + ' ' : ''}modules`;
      }
      return "Confirm selection";
    },
    selectableModules() {
      return getSelectableModules(this.program, this.preferences, {
        markedModules: this.markedModules,
        hiddenModules: this.$props.hiddenModules,
        oneModulePerMockName: this.oneModulePerMockName,
        moduleInput: this.moduleInput
      });
    },
    prioritiesOfStructureElements() {
      return prioritiesOfStructureElements(this.program, this.preferences);
    },
    modulesWithStructures() {
      return modulesWithStructures(this.program, this.preferences, this.prioritiesOfStructureElements);
    },
    selectedStructures() {
      return selectedStructures(this.program, this.preferences);
    },
    groupedModules() {
      return getGroupedModules(this.program, this.preferences, {
        selectableAvailableModules: this.selectableAvailableModules,
        selectableUnavailableModules: this.selectableUnavailableModules,
        modulesWithStructures: this.modulesWithStructures,
        extraGroup: this.extraGroup
      });
    },
    hasStructureGroups() {
      return this.groupedModules.some(group => group.structure && !group.isUnavailable);
    },
    selectableAvailableModules() {
      return this.selectableModules.filter(module => 
        !this.unavailableModules.includes(module.id)
      );
    },
    selectableUnavailableModules() {
      return this.selectableModules.filter(module => 
        this.unavailableModules.includes(module.id)
      );
    },
    displayedMarkedModules() {
      let markedModules = this.program.modules.filter(module => this.markedModules.includes(module.id));

      if (!this.oneModulePerMockName) {
        return markedModules.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      }

      // Create a set of module names from addedModules that are not in markedModules
      const excludedModuleNames = new Set(
          this.program.modules
              .filter(module => this.addedModules.includes(module.id) && !this.markedModules.includes(module.id))
              .map(module => module.name)
      );

      // Only return modules that are unique in their name, but are not removed from marked while Modal has been open
      const uniqueModuleNames = new Set();
      return markedModules.filter(module => {
        const isUnique = !uniqueModuleNames.has(module.name);
        const isNotExcluded = !excludedModuleNames.has(module.name);

        if (isUnique && isNotExcluded) {
          uniqueModuleNames.add(module.name);
          return true;
        }

        return false;
      }).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    },
    changeDetected() {
      if (this.$props.addedModules.length !== this.markedModules.length) {
        return true;
      }

      let countMap = {};

      // Count the occurrences of each string in the first array
      for (let i = 0; i < this.$props.addedModules.length; i++) {
        countMap[this.$props.addedModules[i]] = (countMap[this.$props.addedModules[i]] || 0) + 1;
      }

      // Subtract the count for each string in the second array
      for (let i = 0; i < this.markedModules.length; i++) {
        if (!countMap[this.markedModules[i]]) {
          return true; // string found in this.markedModules that's not in this.$props.addedModules
        }
        countMap[this.markedModules[i]]--;
      }

      // Check if all counts are back to zero
      for (let key in countMap) {
        if (countMap[key] !== 0) {
          return true;
        }
      }

      return false;
    },
    confirmationDisabled() {
      return !(this.$props.confirmationAlwaysEnabled || this.changeDetected);
    }
  },
  watch: {
    currentModal(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.cleanModal();
      }
    }
  },
  methods: {
    initialData() {
      let initialMarked = this.$props.addedModules ? [...this.$props.addedModules] : [];
      
      if (this.$props.extraGroup && this.$props.extraGroup.moduleids && this.$props.markExtraGroup) {
        this.$props.extraGroup.moduleids.forEach(id => {
          if (!initialMarked.includes(id)) {
            initialMarked.push(id);
          }
        });
      }

      return {
        moduleInput: "",
        showDropdown: false,
        markedModules: initialMarked,
        message: '',
        fadeOut: false,
        forceCloseDropdown: false
      };
    },
    clickMarkModule(event, module) {
      if ((event=='infomodal' || event.button === 0 )&& !this.unavailableModules.includes(module.id)) { 
        this.markModule(module);
        this.message = `Module added`;
        this.fadeOut = false;

        setTimeout(() => {
          this.fadeOut = true;
          setTimeout(() => {
            this.message = '';
          }, 0);
        }, 500);
        this.moduleInput = "";
        
        this.forceCloseDropdown = true;
        setTimeout(() => { this.forceCloseDropdown = false; }, 100);
      }
    },
    markModule(module) {
      if (!this.markedModules.includes(module.id)) {
        this.markedModules.push(module.id);
      }

      // If there are submodules, and they are not already selected, add them to the addedModules list
      if (module.submodules.length > 0) {
        module.submodules.forEach(submodule => {
          if (!this.markedModules.includes(submodule)) {
            this.markedModules.push(submodule);
          }
        });
      }

      // mark parent module if min number of marked submodules fulfilled
      if (module.submodule) {
        // find parent module
        const parentModule = this.program.modules.find(module => module.submodules.includes(module.id))
        if (!parentModule) return;
        // check if condition is fulfilled
        const numChildrenSelected = this.markedModules.filter(item => parentModule.submodules.includes(item)).length;
        if (!this.markedModules.includes(parentModule.id) && numChildrenSelected >= parentModule.numRequiredSubmodules) {
          this.markedModules.push(parentModule.id);
        }
      }

      // If there are dependent modules, also add them
      const dependees = this.getDependentModules(module)
      if (dependees) {
        dependees.forEach(dependee => {
          if (!this.markedModules.includes(dependee.id)) {
            this.markedModules.push(dependee.id);
          }
        });
      }
    },

    unmarkModule(module) {
      if (!this.markedModules.includes(module.id)) {
        return
      }
      this.markedModules = this.markedModules.filter(mod => mod !== module.id);
      // unmark parent module if condition not fulfilled anymore, because of possible earlier auto-addition
      if (module.submodule) {
        // find parent module
        const parentModule = this.program.modules.find(module => module.submodules.includes(module.id))
        if (!parentModule) return;
        // check if condition still fulfilled
        const numChildrenSelected = this.markedModules.filter(item => parentModule.submodules.includes(item)).length;
        if (this.markedModules.includes(parentModule.id) && numChildrenSelected < parentModule.numRequiredSubmodules) {
          // unmark parent if condition not fulfilled anymore
          this.markedModules = this.markedModules.filter(mod => mod !== parentModule.id);
        }
      }
    },
    getDependentModules(module) {
      return getDependentModules(module, this.program.modules);
    },
    structureColorClass(moduleId) {
      return getStructureColorClass(moduleId, this.modulesWithStructures);
    },
    highlightClass(moduleId) {
      if (this.highlightDarkBlue && this.highlightDarkBlue.includes(moduleId)) {
        return 'highlighted-module-dark-blue';
      }
      if (this.highlightBlue && this.highlightBlue.includes(moduleId)) {
        return 'highlighted-module-blue';
      }
      return '';
    },
    moduleLocation(module) {
      /*/switch(module.sessions.length==0? '': module.sessions[0].location){
        case 'Garching':
          return 'GAR';
        case 'Straubing':
          return 'STR';
        case 'Ottobrunn':
          return 'OTT';
        case 'Freising':
          return 'FRE';
        case 'Hochbrueck':
          return 'HOB';
        case 'Munich':
          return 'MUC';
        default:
          return '';
      }/*/
      return module.sessions.length==0? '': module.sessions[0].location;
    },
    reload() {
      this.$store.dispatch("requestSchedule");
    },
    confirm() {
      this.$emit("confirmOperation", this.markedModules);
      this.reload();
    },
    cleanModal() {
      Object.assign(this.$data, this.initialData());
    },
    toggleGroup(groupId, event) {
      event.stopPropagation();
      event.preventDefault();
      
      this.collapsedGroups = {
        ...this.collapsedGroups,
        [groupId]: !this.collapsedGroups[groupId]
      };
    },
    isGroupCollapsed(group) {
      const groupId = (group.structure || group.isUnavailable)? `structure-${group.structure.id}` : 'no-structure';
      return this.collapsedGroups[groupId] || false; 
    },
    getGroupId(group) {
      return (group.structure || group.isUnavailable)? `structure-${group.structure.id}` : 'no-structure';
    },
    toggleFocusInput() {
      this.showDropdown ? this.$refs.input.blur() : this.$refs.input.focus();
    },
    openModuleInfoModal(module,inBaseModuleModal) {
      this.selectedModule = module;
      this.currentModuleIndex = null;
      this.openedInBaseModuleModal = inBaseModuleModal;
      this.isModuleInfoModalOpen = true;
    },
    selectModule(moduleId) {
      this.clickMarkModule('infomodal', this.program.modules.find(module => module.id === moduleId));
      this.showDropdown = false;
    },
    handleSpecifyModule({ mockModuleId, realModuleId, semester }) {
      if (mockModuleId) {
        this.$store.commit("REMOVE_DESIRED_IN_SEM_MODULE", { desiredInSemModule: mockModuleId, bulk: true });
        this.$store.commit("REMOVE_DESIRED_MODULE", { desiredModule: mockModuleId, bulk: true });
      }
      if (realModuleId) {
        this.$store.commit("REMOVE_UNDESIRED_MODULE", { undesiredModule: realModuleId, bulk: true });
      }
      if (semester) {
        this.$store.commit('ADD_DESIRED_IN_SEM_MODULE', { module: realModuleId, semester: semester, bulk: true });
      } else {
        this.$store.commit('ADD_DESIRED_MODULE', { desiredModule: realModuleId, bulk: true });
      }
      this.$store.commit('SAVE_STATE');
      this.closeModuleInfoModal();
      this.reload();
    },
    closeModuleInfoModal() {
      this.isModuleInfoModalOpen = false;
      this.selectedModule = null;
      this.showDropdown=true;
    },
  },
};
</script>

<style scoped>
.message-wrapper {
  display: flex;
  flex-direction: row;
  gap: 10px;
  justify-content: flex-end;
}
.list-detail:last-child {
  padding-right: 10px;
}

.list-detail {
  color: #333333;
  font-size: 80%;
  text-align: right;
}

.list-detail-loc {
  width: 50px;
  text-align: left;
  margin-right: 0px;
}

.list-detail-lang,
.list-detail-ects {
  width: 50px;
}


.modal-result {
  flex-grow: 1;
  border: 1px dashed #bbb;
  border-radius: 4px;
  padding: 12px;
  overflow-y: auto;
  min-height: 0;
}

.module-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  font-size: 85%;
  line-height: 140%;
}

.module {
  min-width: 140px;
  height: 96px;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background-color: white;
  margin: 0;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.module:hover {
  border-color: #9abce4;
}

.highlighted-module-dark-blue {
  border: 1.5px solid #5e94d4 !important;
}

.highlighted-module-blue {
  border: 1.5px solid #9abce4 !important;
}

.icon-container {
  display: flex;
  align-items: center;
}

.material-symbols-outlined {
  font-size: 18px;
}

.module .material-symbols-outlined {
  cursor: pointer;
  color: #666;
  transition: color 0.2s;
}

.module .material-symbols-outlined:hover {
  color: #1976d2;
}

.module .tooltip-container .material-symbols-outlined:hover {
  color: #d32f2f;
}

.module-content {
  display: flex;
  height: 100%;
  gap: 6px;
}

.left-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
}
.right-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  flex-shrink: 0;
}

.module-detail {
  line-height: 1.2em;
  min-height: 1.2em;
  font-size: 0.85em;
  color: #666;
}

.module-name {
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
  line-height: 1.25em;
  margin: 2px 0;
  color: #222;
}

.text-hint {
  flex-shrink: 0;
  flex-basis: auto;
  max-height: 3em;
  overflow-y: auto;

  font-size: 90%;
  line-height: 1.5em;
}
.message-wrapper {
  position: absolute;
  width: auto;
  display: flex;
  justify-content: center;
  pointer-events: none;
  z-index: 1000;
  transition: all 0.5s ease;
  bottom: calc(67% + 80px);
  left: 50%;
  transform: translateX(-50%);
}

.message {
  color: #000;
  background-color: #9ABCE4;
  border-radius: 8px;
  padding: 6px 16px;
  transform: translateY(160px);
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  opacity: 1;
  transition: opacity 0.5s;
  pointer-events: auto;
  font-size: 12px;
  font-family: inherit;
  font-weight: bold;
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}
.check-icon {
  width: 20px;
  height: 20px;
  display: inline-block;
  background-color: #28a745;
  mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><path fill="white" d="M13.78 3.72a.75.75 0 10-1.06-1.06L6.5 8.94l-2.22-2.22a.75.75 0 10-1.06 1.06l2.75 2.75c.3.3.77.3 1.06 0l6.75-6.75z"></path></svg>');
  -webkit-mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><path fill="white" d="M13.78 3.72a.75.75 0 10-1.06-1.06L6.5 8.94l-2.22-2.22a.75.75 0 10-1.06 1.06l2.75 2.75c.3.3.77.3 1.06 0l6.75-6.75z"></path></svg>') no-repeat center;
  mask-size: contain;
  -webkit-mask-size: contain;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  line-height: 24px;
  padding: 4px;
  font-size: 14px;
}
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.fade-out {
  opacity: 0; /* Fade out effect */
}
/* Group headers */
.structure-header {
  background-color: #fff8f2;  
  color: #e65100;            
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.2s ease;
  user-select: none;
}

.structure-header:hover {
  background-color: #ffebd8;  
}

.header-text {
  flex-grow: 1;
}

.expand-icon {
  color: #e65100;
  font-size: 20px;
  margin-left: 8px;
  transition: transform 0.2s ease;
  pointer-events: none; 
}

.info-icon {
  cursor: pointer;
  opacity: 0.6;
  margin-left: 5px;
  font-size: 22px !important;
  vertical-align: middle;
  z-index: 900;

}


.suggestion-item {
  padding: 10px 16px 10px 24px; 
  border-bottom: 1px solid #f5f5f5;
  background-color: white;
}

.suggestion-item:hover {
  background-color: #f9f9f9;
}

.module-info-height {
  max-height: 80vh;
  overflow-y: auto;
  width: 600px;
  max-width: 90%;
}

.modal-body {
  padding: 20px;
}

.modal-body h3 {
  margin-top: 15px;
  margin-bottom: 5px;
  color: #333;
  font-size: 16px;
}

.modal-body p {
  margin-top: 0;
  line-height: 1.5;
}

.module-description,
.module-lecturer,
.module-semester,
.module-requirements,
.module-content {
  margin-bottom: 15px;
}

</style>

<style>
.base-module-modal .modal-container,
.base-module-modal .modal-content { 
  width: 85vw !important; 
  max-width: 950px !important; 
  height: 78vh !important;
  max-height: 700px !important;
}

.base-module-modal .modal-body {
  overflow: hidden !important; 
  flex: 1;          
  display: flex;
  flex-direction: column;
  min-height: 0;   
}
</style>